import {
  HomeworkMaterialFileIds,
  HomeworkMaterialMediaRefs,
  HomeworkTaskText,
  type HomeworkMaterialMediaRef,
} from "@edu/shared";

/**
 * «Задать ещё раз» с карточки выданной домашки (1.7
 * `docs/homework-scratch-design.md`): форма открывается с теми же текстом
 * задания и картинками, уже отмеченными. Задания НЕ копируются: их источник
 * выбирается заново, чтобы у нового ученика был свой вариант.
 *
 * Едет состоянием перехода, а не адресом: текст до 10 000 знаков в адрес не
 * помещается, а файлы без текста были бы половиной повтора.
 *
 * ⚠️ `media` это подписи к тем файлам из `fileIds`, что аудио и видео (Р6
 * `docs/homework-media-plan.md`): без них форма нарисовала бы аудио картинкой.
 * Порядок по-прежнему задаёт `fileIds`. Поле необязательно при разборе:
 * история браузера помнит состояния, записанные бандлом до 16.10.2026.
 *
 * ⚠️ `posters` это кадры видео по `fileId` (Р4
 * `docs/homework-media-form-player-plan.md`), отдельным полем, а не в подписи:
 * схема подписи это и форма плана раздачи, куда подписанному пути нельзя, и
 * лишний ключ она молча выбрасывает. Битое поле это «кадров нет», а не отказ
 * повтора: без кадра плитка рисует значок.
 */

export interface RepeatMaterial {
  taskText: string;
  fileIds: string[];
  media: HomeworkMaterialMediaRef[];
  posters: Record<string, string>;
}

/** Состояние перехода для `navigate("/homework/new", { state })`. */
export function repeatState(args: {
  taskText: string | null;
  fileIds: readonly string[];
  media?: readonly HomeworkMaterialMediaRef[];
  posters?: Readonly<Record<string, string>>;
}): { repeat: RepeatMaterial } {
  return {
    repeat: {
      taskText: args.taskText ?? "",
      fileIds: [...args.fileIds],
      media: [...(args.media ?? [])],
      posters: { ...args.posters },
    },
  };
}

/**
 * Разбор состояния перехода. Чужое или битое состояние это «повтора нет», а не
 * поломка формы: историю браузера пишет не только наш код.
 */
export function repeatOf(raw: unknown): RepeatMaterial | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = (raw as { repeat?: unknown }).repeat;
  if (typeof value !== "object" || value === null) return null;
  const text = HomeworkTaskText.safeParse((value as { taskText?: unknown }).taskText);
  const ids = HomeworkMaterialFileIds.safeParse((value as { fileIds?: unknown }).fileIds);
  const media = HomeworkMaterialMediaRefs.optional().safeParse((value as { media?: unknown }).media);
  if (!text.success || !ids.success || !media.success) return null;
  if (text.data.trim() === "" && ids.data.length === 0) return null;
  return {
    taskText: text.data,
    fileIds: ids.data,
    media: media.data ?? [],
    posters: postersOf((value as { posters?: unknown }).posters, ids.data),
  };
}

/** Кадры из состояния перехода: только строки и только для файлов повтора. */
function postersOf(raw: unknown, fileIds: readonly string[]): Record<string, string> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const fileId of fileIds) {
    const path: unknown = Object.hasOwn(raw, fileId) ? (raw as Record<string, unknown>)[fileId] : undefined;
    if (typeof path === "string" && path !== "") out[fileId] = path;
  }
  return out;
}
